import { Info, Volume2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { UseAudioOutputControlResult } from "../hooks/useAudioOutputControl";
import {
  DeviceMenuDivider,
  DeviceMenuHeading,
  DeviceMenuItem,
  type MediaDeviceOption,
} from "./DeviceSelectButton";

/** What the Speakers section needs from the output controls. */
export type SpeakersAudio = Pick<
  UseAudioOutputControlResult,
  "supportsSinkId" | "isIOS" | "mode" | "toggleMode" | "connectedDeviceName" | "volume" | "setVolume" | "volumeFixed"
>;

interface SpeakersSectionProps {
  speakers: MediaDeviceOption[];
  selectedId: string | null;
  onSelect: (deviceId: string) => void;
  /** The live call's output controls. Absent on a pre-join screen: nothing
   *  plays yet, so there's no volume and no Earpiece / Loudspeaker choice. */
  audio?: SpeakersAudio;
  light?: boolean;
  /** Closes the surrounding menu after a device pick. */
  onPicked?: () => void;
}

/** Whether the browser can choose an output device (not Safari / iOS). */
export function browserSupportsSinkId(): boolean {
  return typeof HTMLAudioElement !== "undefined" && "setSinkId" in HTMLAudioElement.prototype;
}

/**
 * The Speakers half of the merged mic menu (GH-1859): the output device list —
 * or, where the browser can't choose an output, a system-settings hint — the
 * iPhone Earpiece / Loudspeaker choice, and the call volume. Speaker on/off and
 * "Test speaker" are gone; volume 0% still silences the call. Pre-join it is
 * the list (or hint) alone.
 */
export function SpeakersSection({ speakers, selectedId, onSelect, audio, light, onPicked }: SpeakersSectionProps) {
  const { t } = useTranslation();
  const muted = light ? "text-muted-foreground" : "text-white/50";
  const showRoute = !!audio && audio.isIOS && !audio.connectedDeviceName;
  const supportsSinkId = audio ? audio.supportsSinkId : browserSupportsSinkId();

  return (
    <div data-speakers-section="">
      <DeviceMenuDivider light={light} />
      <DeviceMenuHeading light={light}>{t("call.controls.devicesSpeakers")}</DeviceMenuHeading>
      {supportsSinkId ? (
        speakers.length === 0 ? (
          <div className={`px-3 py-2 text-sm ${muted}`}>…</div>
        ) : (
          speakers.map((d) => (
            <DeviceMenuItem
              key={d.deviceId}
              label={d.label}
              selected={d.deviceId === selectedId}
              icon={Volume2}
              light={light}
              onClick={() => {
                onSelect(d.deviceId);
                onPicked?.();
              }}
            />
          ))
        )
      ) : (
        <p data-speakers-hint="" className={`flex items-start gap-1.5 px-3 py-2 text-xs ${muted}`}>
          <Info className="size-3.5 shrink-0 mt-px" />
          {t("call.controls.devicesSystemOutputHint")}
        </p>
      )}
      {showRoute &&
        (["earpiece", "speaker"] as const).map((route) => (
          <DeviceMenuItem
            key={route}
            label={route === "earpiece" ? t("call.controls.routeEarpiece") : t("call.controls.routeSpeaker")}
            selected={audio?.mode === route}
            icon={Volume2}
            light={light}
            onClick={() => {
              if (audio && audio.mode !== route) audio.toggleMode();
            }}
          />
        ))}
      {audio && <div className="px-3 pt-2 pb-1.5">
        <div className="flex items-center justify-between mb-1.5">
          <span className={`text-[11px] font-bold ${muted}`}>{t("call.controls.volume")}</span>
          <span className={`text-[11px] font-bold ${light ? "text-foreground" : "text-white/90"}`}>{audio.volume}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={audio.volume}
          onChange={(e) => audio.setVolume(Number(e.target.value))}
          className="w-full accent-primary"
          aria-label={t("call.controls.volume")}
          aria-valuetext={`${audio.volume}%`}
        />
        {audio.volumeFixed && (
          <p className={`mt-1.5 flex items-start gap-1.5 text-[11px] ${muted}`}>
            <Info className="size-3.5 shrink-0 mt-px" />
            {t("call.controls.volumeFixedSpeakerMode")}
          </p>
        )}
      </div>}
    </div>
  );
}
